import { useEffect } from 'react';
import { Box, Group, Loader, Stack, Tabs, Text } from '@mantine/core';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useNavigate, useParams } from 'react-router-dom';
import type { CSSProperties, ReactElement } from 'react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { SurfaceTopicViewModel } from './SurfaceTopicViewModel';
import { findTopicInSurface, type AnalyticsSurface } from './AnalyticsSurface';
import { useSurface } from './useSurface';
import SessionDriftOverviewBody from './SessionDriftOverviewBody';
import { accentTokens } from '@/design/VizAccents';
import { fieldCarriesConfidence, formatCount, formatPercent, metricLabel } from '@/shared/analytics/MetricFormatting';
import SurfaceBreadcrumbRow from './SurfaceBreadcrumbRow';
import PageLevelFilterRow from './PageLevelFilterRow';
import StatBand, { type StatItem } from '@/design/StatBand';
import TopicSummaryPanel from './TopicSummaryPanel';
import SideRailPageLayout from './SideRailPageLayout';
import TopicInsightsPanel from './TopicInsightsPanel';
import TopicViz from './TopicViz';
import TopicComingSoon from './TopicComingSoon';
import TopicNotFound from './TopicNotFound';
import SurfaceAssistScope, { SurfaceAssistToolbar } from './assist/SurfaceAssistScope';
import { useAssistFilters } from './assist/AssistSurfaceProvider';

interface SurfaceTopicPageProps {
    surfaceId: string;
}

export default function SurfaceTopicPage({ surfaceId }: SurfaceTopicPageProps): ReactElement {
    const surface = useSurface(surfaceId);
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <SurfaceTopicRouter surface={surface} />
        </SurfaceAssistScope>
    );
}

/** Session drift gets a purpose-built people-first L1; every other topic renders the generic viz body. */
function SurfaceTopicRouter({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const { topicId } = useParams<{ topicId: string }>();
    const definition = findTopicInSurface(surface, topicId);
    if (definition?.anomalyKind === 'session_drift') {
        return <SessionDriftOverviewBody surface={surface} />;
    }
    return <SurfaceTopicBody surface={surface} />;
}

function SurfaceTopicBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const { topicId } = useParams<{ topicId: string }>();
    const navigate = useNavigate();
    const vm = useViewModel(SurfaceTopicViewModel);
    const filters = useAssistFilters();

    useEffect(() => {
        void vm.load(surface.id, topicId, filters);
    }, [vm, surface.id, topicId, filters]);

    if (vm.notFound) return <TopicNotFound />;

    const definition = vm.definition;
    if (vm.loading || definition === null) {
        return (
            <Group justify="center" gap="sm" py="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                    Loading insight…
                </Text>
            </Group>
        );
    }

    if (definition.comingSoon === true) {
        return <TopicComingSoon surface={surface} definition={definition} />;
    }

    if (vm.primary === null) {
        return (
            <Stack align="center" gap="xs" py="xl">
                <Text size="sm" c="red">
                    {vm.errorMessage ?? 'This insight could not be loaded.'}
                </Text>
            </Stack>
        );
    }

    const tokens = accentTokens(definition.accent);
    const Icon = definition.icon;
    const totals = vm.primary.totals;
    const capitalLens = definition.lensPlural.charAt(0).toUpperCase() + definition.lensPlural.slice(1);

    const stats: StatItem[] = [
        { label: metricLabel('turns'), value: formatCount(totals.turns) },
        { label: metricLabel('sessions'), value: formatCount(totals.sessions) },
        { label: 'Users', value: formatCount(totals.users) },
    ];
    if (definition.vizKind === 'alerts') {
        stats.push(
            definition.anomalyKind === 're_prompt' ?
                { label: 'Re-prompts', value: formatCount((vm.anomalies?.events ?? []).reduce((sum, event) => sum + (event.count ?? 0), 0)) }
            :   { label: capitalLens, value: formatCount(vm.anomalies?.total ?? 0) },
        );
    } else {
        stats.push({ label: capitalLens, value: formatCount(vm.primary.distinctCount) });
    }
    if (fieldCarriesConfidence(definition.primaryField)) {
        stats.push({ label: 'Avg confidence', value: formatPercent(totals.avgConfidence) });
    }

    const sideRail = surface.drillStyle === 'side-rail';

    const iconBox = (
        <Box
            style={{
                width: 44,
                height: 44,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--bm-color-surface)',
                border: '1px solid var(--bm-color-border)',
                borderRadius: 'var(--bm-radius)',
            }}
        >
            <Icon size={22} color="var(--bm-color-brand-accent)" />
        </Box>
    );

    const siblings = definition.navGroup === undefined ? [] : surface.catalog.filter((topic) => topic.navGroup === definition.navGroup);

    const vizSection = (
        <Stack gap="sm">
            {siblings.length > 1 && (
                <Tabs
                    value={definition.id}
                    onChange={(value) => {
                        if (value !== null) navigate(`${surface.basePath}/${value}`);
                    }}
                    style={{ '--tabs-color': tokens.solid } as CSSProperties}
                >
                    <Tabs.List>
                        {siblings.map((topic) => {
                            const TabIcon = topic.icon;
                            return (
                                <Tabs.Tab key={topic.id} value={topic.id} leftSection={<TabIcon size={15} />}>
                                    {topic.navLabel ?? topic.title}
                                </Tabs.Tab>
                            );
                        })}
                    </Tabs.List>
                </Tabs>
            )}
            {definition.vizKind !== 'matrix' && definition.vizKind !== 'trend' && (
                <Group justify="space-between" align="baseline">
                    <Text size="sm" fw={700} style={{ letterSpacing: '-0.02em' }}>
                        {definition.vizKind === 'time-matrix' ?
                            `${capitalLens} × time`
                        : definition.vizKind === 'alerts' ?
                            capitalLens
                        : definition.vizKind === 'user-trend-list' ?
                            'Users by trajectory'
                        :   `By ${definition.lensLabel}`}
                    </Text>
                    <Text size="xs" c="dimmed">
                        Click any {definition.vizKind === 'alerts' ? 'row' : definition.lensLabel} to see the {definition.vizKind === 'alerts' ? 'turns' : 'prompts'} behind it →
                    </Text>
                </Group>
            )}

            <TopicViz definition={definition} primary={vm.primary} matrix={vm.matrix} trend={vm.trend} anomalies={vm.anomalies} userTrends={vm.userTrends} basePath={surface.basePath} />
        </Stack>
    );

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            {!sideRail && <SurfaceBreadcrumbRow items={[{ label: surface.title, to: surface.basePath }, { label: definition.title }]} />}

            {sideRail ?
                <PageHeaderComponent
                    leading={iconBox}
                    eyebrow={surface.title}
                    title={definition.navLabel ?? definition.title}
                    subtitle={definition.question.length > 0 ? definition.question : undefined}
                >
                    {definition.id.startsWith('pinned-') && (
                        <Text size="xs" c="dimmed" mt={4}>
                            Custom view — created with Ask me anything
                        </Text>
                    )}
                </PageHeaderComponent>
            :   <PageHeaderComponent title={definition.title} subtitle={definition.question} leading={iconBox} />}

            {!sideRail && (
                <Box mb="lg">
                    <StatBand stats={stats} />
                </Box>
            )}

            {vm.errorMessage !== null && (
                <Text size="sm" c="red" mb="md">
                    {vm.errorMessage}
                </Text>
            )}

            {sideRail ?
                <SideRailPageLayout
                    rail={
                        <Stack gap="md">
                            <TopicSummaryPanel stats={stats} />
                            <TopicInsightsPanel surfaceTitle={surface.title} definition={definition} primary={vm.primary} derived={vm.callouts} />
                        </Stack>
                    }
                >
                    {vizSection}
                </SideRailPageLayout>
            :   <>
                    {vizSection}
                    <Box mt="lg">
                        <TopicInsightsPanel surfaceTitle={surface.title} definition={definition} primary={vm.primary} derived={vm.callouts} />
                    </Box>
                </>
            }
        </Box>
    );
}
